import { useRef } from 'react'
import { faWavePulse } from '@fortawesome/pro-regular-svg-icons'
import Typography from '@mui/material/Typography'
import { FundingTimelineEvent } from '../../api/fundingApi'
import { formatActivityTime } from '../../utils/format'
import { ScrollHint } from '../ScrollHint/ScrollHint'
import { SectionCard } from './SectionCard'
import { ReviewWithAiButton } from './ReviewWithAiButton'
import { useStyles } from './RecentActivities.styles'

interface RecentActivitiesProps {
  events: FundingTimelineEvent[]
  onReview: () => void
}

const FAILURE_PATTERN = /fail|error|insufficient|reject|declin/i

const isFailure = (event: FundingTimelineEvent) =>
  FAILURE_PATTERN.test(event.summary ?? '')

export const RecentActivities = ({
  events,
  onReview,
}: RecentActivitiesProps) => {
  const { classes, cx } = useStyles()
  const listRef = useRef<HTMLOListElement>(null)

  return (
    <SectionCard
      icon={faWavePulse}
      title="Recent activities of this funding request"
      testId="funding-detail-activities"
    >
      {events.length === 0 ? (
        <Typography component="p" className={classes.empty}>
          No recent activity available.
        </Typography>
      ) : (
        <div className={classes.scrollWrap}>
          <ol className={classes.timeline} ref={listRef}>
            {events.map((event, index) => {
              const failed = isFailure(event)
              const time = formatActivityTime(event.event_at)
              const isLast = index === events.length - 1
              return (
                <li className={classes.event} key={event.id}>
                  <div className={classes.rail}>
                    <span
                      className={cx(
                        classes.dot,
                        failed ? classes.dotFailure : classes.dotSuccess
                      )}
                    />
                    {!isLast && <span className={classes.line} />}
                  </div>
                  <div className={classes.content}>
                    <Typography component="span" className={classes.summary}>
                      {event.summary || '-'}
                    </Typography>
                    <div className={classes.meta}>
                      <Typography component="span" className={classes.actor}>
                        {event.actor || '-'}
                      </Typography>
                      {time && (
                        <Typography component="span" className={classes.time}>
                          {time}
                        </Typography>
                      )}
                    </div>
                    {failed && (
                      <div className={classes.review}>
                        <ReviewWithAiButton onClick={onReview} />
                      </div>
                    )}
                  </div>
                </li>
              )
            })}
          </ol>
          <ScrollHint scrollRef={listRef} contentKey={events.length} />
        </div>
      )}
    </SectionCard>
  )
}
